import { Injectable, OnDestroy } from '@angular/core';

/**
 * Announces text to screen readers through a single, permanent ARIA live region.
 *
 * The region is created once and left in the DOM for the life of the app, and is torn down
 * only when the root injector is destroyed. That matters for SPA route changes: screen
 * readers register live regions while building their virtual buffer, so a region that is
 * created and torn down around a navigation is never registered and the message is dropped
 * silently (WCAG 2.1 SC 4.1.3 Status Messages).
 *
 * A message raised while another is still being announced is held back rather than
 * overwriting it. Only one message is held: these are *status* messages, so a newer status
 * supersedes an older one that has not been spoken yet. Announcing every message in order
 * would queue stale text - a submit raises 'Submitting...', two route titles and a
 * confirmation in quick succession, and the confirmation is the only one still worth
 * hearing by the time the earlier three have been read out.
 */
@Injectable({
  providedIn: 'root'
})
export class AccessibilityService implements OnDestroy {
  /** delay between clearing and writing so the reader observes a real mutation */
  private static readonly MUTATION_DELAY_MS = 100;
  /** how long a message is left in the region before a held message replaces it */
  private static readonly MESSAGE_DWELL_MS = 1200;

  private liveRegion: HTMLElement = null;
  /** message currently in the region, or null when nothing is being announced */
  private announcing: string = null;
  /** the single message waiting its turn. A newer one replaces it */
  private pending: string = null;
  private timers: number[] = [];

  constructor() {}

  /** Creates the live region if it is not already in the DOM. Safe to call repeatedly. */
  ensureLiveRegion(): HTMLElement {
    if (this.liveRegion?.isConnected) {
      return this.liveRegion;
    }

    const el = document.createElement('div');
    el.setAttribute('id', 'lineup-live-announcer');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'assertive');
    el.setAttribute('aria-atomic', 'true');
    el.classList.add('screen-reader-only');
    document.body.appendChild(el);

    this.liveRegion = el;
    return el;
  }

  talk(text: string) {
    const message = text?.trim();
    if (!message) {
      return;
    }

    //make sure the region is registered with the reader before anything is written to it
    this.ensureLiveRegion();

    if (message === this.announcing || message === this.pending) {
      return; //already being said, or already waiting to be said
    }

    if (this.announcing === null) {
      this.announce(message);
    } else {
      this.pending = message; //supersedes any older status still waiting
    }
  }

  private announce(message: string) {
    this.announcing = message;

    const region = this.ensureLiveRegion();
    region.textContent = ''; //force a mutation even when the same text repeats

    this.defer(() => (region.textContent = message), AccessibilityService.MUTATION_DELAY_MS);
    this.defer(() => {
      this.announcing = null;
      const next = this.pending;
      this.pending = null;
      if (next) {
        this.announce(next);
      }
    }, AccessibilityService.MUTATION_DELAY_MS + AccessibilityService.MESSAGE_DWELL_MS);
  }

  private defer(action: () => void, delay: number) {
    const id = window.setTimeout(() => {
      this.timers = this.timers.filter((timer) => timer !== id);
      action();
    }, delay);
    this.timers.push(id);
  }

  ngOnDestroy() {
    this.timers.forEach((id) => window.clearTimeout(id));
    this.timers = [];
    //clear the in-flight state too. A timer that would have reset it is now cancelled
    this.announcing = null;
    this.pending = null;
    this.liveRegion?.remove();
    this.liveRegion = null;
  }
}
